<!-- First fetch still in flight: the stored data is empty until it answers,
     so the component itself would only say "No data available". -->
<div class="dsg-widget-loading" [class.is-compact]="isInputControl()" *ngIf="showLoading(); else widgetBody" role="status" aria-live="polite">
  <span class="dsg-loading-badge">
    <span class="dsg-loading-ring" aria-hidden="true"></span>
    <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
  </span>
  <span class="dsg-widget-loading-text">Loading data…</span>
  <small class="dsg-widget-loading-hint" *ngIf="title()">{{ title() }}</small>
</div>

<ng-template #widgetBody>
<!-- No rows for the active filters: never the design's stored rows or an earlier filter's. -->
<div class="dsg-widget-placeholder dsg-widget-no-data" *ngIf="showNoData(); else placeholderOrLive" role="status">
  <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
  <span class="dsg-widget-placeholder-label">{{ title() || label() }}</span>
  <small class="dsg-widget-placeholder-hint">No data available</small>
</div>
</ng-template>

<ng-template #placeholderOrLive>
<!-- Placeholder: shown while dragging/resizing, and before any data is bound.
     Cheap to render, so the drag loop never touches ECharts. -->
<div class="dsg-widget-placeholder" *ngIf="showPlaceholder(); else liveWidget">
  <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
  <span class="dsg-widget-placeholder-label">{{ title() || label() }}</span>
  <small class="dsg-widget-placeholder-hint" *ngIf="!suspended()">Bind data in the inspector</small>
</div>
</ng-template>

<!-- The real component. Same mapping as the DVM Layout Builder's tileBody. -->
<ng-template #liveWidget>
  <div class="dsg-widget-live" [class.is-control]="isInputControl()" [class.is-recomputing]="isRecomputing()" [ngSwitch]="layoutType()"
    [class.is-field-fill]="fieldFillHeight()" [style.--dvm-filter-control-height]="fieldHeightVar()">
    <!-- Refreshing data that is already on screen: keep it visible underneath. -->
    <div class="dsg-widget-loading is-overlay" [class.is-compact]="isInputControl()" *ngIf="isRecomputing()" role="status" aria-live="polite">
      <span class="dsg-loading-badge">
        <span class="dsg-loading-ring" aria-hidden="true"></span>
        <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
      </span>
      <span class="dsg-widget-loading-text">Updating data…</span>
    </div>

    <app-waterfall-chart *ngSwitchCase="'WATERFALL'" [data]="widgetData()" (crossFilterChange)="onCrossFilter($event)"></app-waterfall-chart>

    <lib-drill-down *ngSwitchCase="'DRILL_DOWN_TABLE'" [data]="widgetData()"></lib-drill-down>

    <app-unified-table-matrix-chart
      *ngSwitchCase="'TABLE_MATRIX'"
      [data]="widgetData()"
      (crossFilterChange)="onCrossFilter($event)"
      (navigate)="runtime?.navigateToDashboard($event.targetDashboard, $event)"
      (cellEdit)="onTableCellEdit($event)"
      (cellButton)="onTableCellButton($event)"
    ></app-unified-table-matrix-chart>

    <app-nested-table *ngSwitchCase="'NESTED_TABLE'" [data]="$any(widgetData())" (cellChange)="onNestedCellChange($event)" (action)="onNestedAction($event)" (cellButton)="onNestedCellButton($event)" (valueClick)="runtime?.toggleCrossFilter(nodeId(), $event.field, $event.value)" (rowSave)="onNestedRowSave($event)" [saveRequest]="runtime?.tableSaveRequests()?.[nodeId()]?.seq" (saveAllDone)="runtime?.finishTableSave(nodeId(), $event)"></app-nested-table>

    <app-unified-bar-column-chart *ngSwitchCase="'BAR'" [data]="widgetData()" (crossFilterChange)="onCrossFilter($event)"></app-unified-bar-column-chart>

    <app-unified-line-area-chart *ngSwitchCase="'LINE_AREA'" [data]="widgetData()" (crossFilterChange)="onCrossFilter($event)"></app-unified-line-area-chart>

    <app-unified-scatter-bubble-chart *ngSwitchCase="'SCATTER_BUBBLE'" [data]="widgetData()" (crossFilterChange)="onCrossFilter($event)"></app-unified-scatter-bubble-chart>

    <app-unified-pie-composition-chart *ngSwitchCase="'PIE_COMPOSITION'" [data]="widgetData()" (crossFilterChange)="onCrossFilter($event)"></app-unified-pie-composition-chart>

    <app-horizontal-bar-chart *ngSwitchCase="'HORIZONTAL_BAR_CHART'" [data]="widgetData()"></app-horizontal-bar-chart>

    <app-number-stats-shared *ngSwitchCase="'KPI'" [data]="widgetData()"></app-number-stats-shared>

    <app-input-box *ngSwitchCase="'INPUT_BOX'" [data]="widgetData()" (valueChange)="onUserValue($event)" (save)="onInputSave($event)"></app-input-box>

    <app-mat-slider *ngSwitchCase="'SLIDER'" [data]="widgetData()" (valueChange)="onSliderUserValue($event)"></app-mat-slider>

    <!-- Static settings from the inspector, with the Data tab's dataset-bound
         value / range / zones folded in (see zone-slider-binding.utils). -->
    <app-zone-slider *ngSwitchCase="'ZONE_SLIDER'" [data]="zoneSliderData()" (valueChange)="onZoneSliderChange($event)"></app-zone-slider>

    <!-- Options come from the `[options]` input, NOT from `data` — without it
         the control renders as an empty dropdown. -->
    <app-global-dropdown
      *ngSwitchCase="'DROPDOWN'"
      [data]="widgetData()"
      [options]="dropdownOptions()"
      [ngModel]="controlValue()"
      (ngModelChange)="runtime?.onDropdownChange(nodeId(), $event)"
    ></app-global-dropdown>

    <!-- This one takes discrete @Inputs rather than a [data] bag. -->
    <app-dvm-file-upload
      *ngSwitchCase="'FILE_UPLOAD'"
      [title]="title()"
      [buttonLabel]="uploadLabel()" [buttonStyle]="uploadButtonStyle()"
      [accept]="uploadAccept()"
      [uploading]="isRecomputing()"
      [message]="uploadMessage()"
      (filePicked)="runtime?.onFilePicked(nodeId(), $event)"
    ></app-dvm-file-upload>

    <app-dvm-popup *ngSwitchCase="'POPUP'" [data]="widgetData()" (navigateTo)="runtime?.navigateToDashboard($event)"></app-dvm-popup>

    <!-- One feature per button; the runtime runs it (popups open in the button). -->
    <app-dvm-action-button
      *ngSwitchCase="'BUTTON'"
      [data]="widgetData()"
      [busy]="!!buttonState()?.busy"
      [disabled]="runtime?.approvalHasNegativeValue(nodeId()) ?? false"
      [active]="!!buttonState()?.active"
      [message]="buttonState()?.message ?? ''"
      [messageKind]="buttonState()?.kind ?? ''"
      [popupWidgetData]="buttonPopupData()"
      (pressed)="runtime?.onButtonClick(nodeId())"
      (navigateTo)="runtime?.navigateToDashboard($event)"
    ></app-dvm-action-button>

    <!-- A layoutType the config knows about but this host has no branch for.
         Renders the placeholder rather than an empty box, so it is obvious
         which component still needs wiring up. -->
    <div class="dsg-widget-placeholder is-unknown" *ngSwitchDefault>
      <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
      <span class="dsg-widget-placeholder-label">{{ label() }}</span>
      <small class="dsg-widget-placeholder-hint">No renderer registered</small>
    </div>
  </div>
</ng-template>

<!-- The last table-cell button's result ("Submitted", an error ...), on the table it was clicked in. -->
<div class="dsg-cell-button-note" *ngIf="cellButtonNote() as note" [class.is-error]="note.kind === 'error'" role="status">{{ note.busy ? 'Working…' : note.message }}</div>
